/**
 * <AmountWithNative /> - iter1092 (Phase B)
 *
 * Renders any document / report amount using the platform's
 * multi-currency convention:
 *
 *   £79.00                              ← primary (native currency)
 *   = $106.49 @ 1.348 on 04/08/2026     ← subtle sub-line (functional)
 *
 * When the amount's native currency equals the business's functional
 * currency, only the primary line renders (no sub-line noise). When
 * the caller has an explicit exchange rate and rate-date on the doc
 * (e.g. bills, invoices, expenses store `exchange_rate` and
 * `exchange_rate_date`), they're surfaced in the sub-line.
 *
 * Usage:
 *   <AmountWithNative
 *     native={item.amount}
 *     nativeCurrency={item.currency}
 *     functional={item.amount_functional}
 *     functionalCurrency={business?.currency}
 *     exchangeRate={item.exchange_rate}
 *     exchangeRateDate={item.exchange_rate_date}
 *     primarySize="text-base font-semibold"
 *     secondarySize="text-[11px] text-muted-foreground"
 *   />
 */
import React from 'react';
import { formatCurrency } from '../utils/currency';

const AmountWithNative = ({
  native,
  nativeCurrency,
  functional,
  functionalCurrency,
  exchangeRate,
  exchangeRateDate,
  primarySize = 'text-base font-semibold text-foreground',
  secondarySize = 'text-[11px] text-muted-foreground',
  className = '',
  align = 'right',
  showRate = true,
  showRateDate = false,
  testId,
}) => {
  const nc = (nativeCurrency || '').toUpperCase();
  const fc = (functionalCurrency || '').toUpperCase();
  const nativeAmount = native != null ? native : (functional != null ? functional : 0);
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

  const primary = formatCurrency(nativeAmount, nc || fc);

  const showSecondary =
    nc && fc && nc !== fc && functional != null && !Number.isNaN(Number(functional));

  let secondary = null;
  if (showSecondary) {
    secondary = `≈ ${formatCurrency(functional, fc)}`;
    if (showRate && exchangeRate) {
      secondary += ` @ ${Number(exchangeRate).toFixed(3)}`;
    }
    if (showRateDate && exchangeRateDate) {
      const d = String(exchangeRateDate).slice(0, 10);
      secondary += ` on ${d}`;
    }
  }

  return (
    <div className={`${alignClass} ${className}`} data-testid={testId}>
      <div className={primarySize}>{primary}</div>
      {showSecondary && <div className={secondarySize}>{secondary}</div>}
    </div>
  );
};

export default AmountWithNative;
export { AmountWithNative };
